import { ReactNode, RefObject, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { DateInputProps } from '../DateInput.types';

export interface CalendarPopoverProps {
  anchorRef: RefObject<HTMLDivElement | null>;
  children: ReactNode;
  id: string;
  placement: DateInputProps['popperPlacement'];
}

interface PopoverPosition {
  left: number;
  top: number;
}

const VIEWPORT_MARGIN = 8;
const ANCHOR_GAP = 4;

export const CalendarPopover = ({ anchorRef, children, id, placement }: CalendarPopoverProps) => {
  const popoverRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<PopoverPosition | null>(null);

  useLayoutEffect(() => {
    if (!popoverRef.current) return undefined;

    const updatePosition = () => {
      if (!popoverRef.current) return;
      const anchor = anchorRef.current?.querySelector<HTMLElement>('[data-date-input-trigger]');
      if (!anchor) return;

      const anchorRect = anchor.getBoundingClientRect();
      const popoverRect = popoverRef.current.getBoundingClientRect();
      const availableAbove = anchorRect.top - VIEWPORT_MARGIN;
      const availableBelow = window.innerHeight - anchorRect.bottom - VIEWPORT_MARGIN;
      const prefersTop = placement?.startsWith('top');
      const shouldOpenAbove = prefersTop
        ? availableAbove >= popoverRect.height || availableAbove > availableBelow
        : availableBelow < popoverRect.height && availableAbove > availableBelow;

      let top = shouldOpenAbove
        ? anchorRect.top - popoverRect.height - ANCHOR_GAP
        : anchorRect.bottom + ANCHOR_GAP;
      let left = placement?.endsWith('end')
        ? anchorRect.right - popoverRect.width
        : anchorRect.left;

      if (left + popoverRect.width > window.innerWidth - VIEWPORT_MARGIN) {
        left = anchorRect.right - popoverRect.width;
      }

      top = Math.max(
        VIEWPORT_MARGIN,
        Math.min(top, window.innerHeight - popoverRect.height - VIEWPORT_MARGIN),
      );
      left = Math.max(
        VIEWPORT_MARGIN,
        Math.min(left, window.innerWidth - popoverRect.width - VIEWPORT_MARGIN),
      );

      setPosition({ left, top });
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [anchorRef, placement]);

  if (typeof document === 'undefined') return null;

  return createPortal(
    <div
      ref={popoverRef}
      id={id}
      role="dialog"
      aria-label="Choose date"
      style={{
        left: position?.left ?? 0,
        top: position?.top ?? 0,
        visibility: position ? 'visible' : 'hidden',
      }}
      className="fixed z-[10000000] w-72 max-w-[calc(100vw-1rem)] rounded-md border border-solid border-secondary-300 bg-secondary-100 p-3 text-text-primary shadow-primary-outset"
    >
      {children}
    </div>,
    document.body,
  );
};
